/**
 * The site's language when nothing else names one (LanguageProvider's fallback). Only the Vite dev server and
 * tests reach it: in production the Koa server answers every address without a `/<lang>/` prefix with a
 * redirect to a prefixed one (server/visitorLanguage.mjs, fallback `en` there), so the app always starts on
 * a prefixed address.
 */
export const SITE_FALLBACK_LANGUAGE = 'cs';

/**
 * The language a visit starts in: the `/<lang>/` prefix of the path when it is a site language (`supported`),
 * else the `?lang=` query, else the browser's language — the first of these two that is a language code at
 * all (`known`, as lib.spa-lang's getDefaultLanguage); if that one isn't a site language, the fallback. The
 * language saved in localStorage by the old switcher is no longer an input: the address names the language.
 *
 * Kept as a pure function so the app and the inline preload script in index.html (inlinePreloadScript)
 * agree on it.
 */
export function pickInitialLanguage(
  pathname: string | null | undefined,
  query: string | null | undefined,
  browser: string | null | undefined,
  known: readonly string[],
  supported: readonly string[],
  fallback: string,
): string {
  const prefix = pathname?.split('/')[1];
  if (prefix && supported.includes(prefix)) {
    return prefix;
  }
  const candidate = [query, browser?.slice(0, 2)].find((code) => !!code && known.includes(code));

  return candidate && supported.includes(candidate) ? candidate : fallback;
}

/**
 * Inline script for index.html that starts downloading this visitor's translation file next to the bundle,
 * instead of after it has run (vite.preload.plugin.ts). Plain ES5 with the lists baked in, as it runs
 * before any module. It chooses like pickInitialLanguage with the site's languages as the known ones
 * (lib.common's list of all codes isn't available there), checked in initialLanguage.test.ts; they differ
 * only for a `?lang=` code the site doesn't offer, and a wrong guess only costs an unused preload.
 */
export function inlinePreloadScript(supported: readonly string[], fallback: string): string {
  const list = JSON.stringify(supported);

  return `(function(){var s=${list},p=location.pathname.split('/')[1],q=null;try{q=new URLSearchParams(location.search).get('lang')}catch(e){}var b=(navigator.language||'').slice(0,2),c=[p,q,b].filter(function(x){return x&&s.indexOf(x)>-1})[0]||${JSON.stringify(fallback)},k=document.createElement('link');k.rel='preload';k.as='fetch';k.crossOrigin='anonymous';k.href='/translations/'+c+'.json';document.head.appendChild(k)})();`;
}
